<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import V2SetupPickerList from '@/components/v2/store-setup/shared/V2SetupPickerList.vue';
  import type { IPickerOption } from '@/domain/store-setup/location-step';
  import { useCajaSelection } from '@/composition/use-caja-selection';
  import { ICaja } from '@/interfaces/ICaja';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import V2ShellBranchPicker from './V2ShellBranchPicker.vue';

  /**
   * Branch and caja of the pages that work on one of them (plan §4.2): a
   * white card on the canvas with two pickers and the caja's state.
   */
  const props = defineProps<{
    showBranch: boolean;
    showCaja: boolean;
  }>();

  const restaurantStore = useRestaurantStore();
  const { selectCaja } = useCajaSelection();

  const branches = computed(() =>
    (restaurantStore.branches ?? []).filter((branch) => branch.active)
  );
  const cajas = computed<ICaja[]>(() =>
    (restaurantStore.cajas ?? []).filter((caja) => caja.enabled)
  );
  const caja = computed(() => restaurantStore.caja);
  const cajaPath = computed(() =>
    restaurantStore.restaurant
      ? `/${restaurantStore.restaurant.stringId}/caja`
      : null
  );

  /** Lists this long get a search box (as the branch picker). */
  const SEARCHABLE_FROM = 9;

  const options = computed<IPickerOption[]>(() =>
    cajas.value.map((option) => ({
      value: String(option.id),
      label: option.name,
    }))
  );
  const statusById = computed(
    () =>
      new Map(cajas.value.map((option) => [String(option.id), option.status]))
  );

  /** The badge: open, closed, or asking to pick one. */
  const status = computed<'open' | 'closed' | 'none'>(() =>
    caja.value ? caja.value.status : 'none'
  );
  const STATUS_KEYS = {
    open: 'posV2.cajaOpen',
    closed: 'posV2.cajaClosed',
    none: 'shell.caja.status.none',
  } as const;

  function pick(value: string, close: () => void) {
    close();
    const target = cajas.value.find((option) => String(option.id) === value);
    if (!target || target.id === caja.value?.id) return;
    // Asks for the caja key; the store only changes when it is right.
    selectCaja(target);
  }
</script>

<template>
  <div
    v-if="showBranch || showCaja"
    class="v2-shell-context-bar hidden-print-only"
  >
    <V2ShellBranchPicker
      v-if="showBranch"
      class="bar-item"
      :branches="branches"
      :branch="restaurantStore.selectedBranch"
      :prefix="$t('branch')"
    />

    <template v-if="showCaja">
      <PosUiPopover placement="bottom" align="start" class="bar-item">
        <template #trigger="{ open, toggle }">
          <button
            type="button"
            class="caja-trigger"
            aria-haspopup="listbox"
            :aria-expanded="String(open)"
            @click="toggle"
          >
            <PosUiIcon name="cash-register" class="trigger-icon" />
            <span class="trigger-label">
              <span class="trigger-prefix">{{ $t('posV2.cajaLabel') }}:</span>
              {{ caja ? caja.name : '—' }}
            </span>
            <PosUiIcon name="chevron-down" class="trigger-chevron" />
          </button>
        </template>
        <template #default="{ close }">
          <V2SetupPickerList
            :options="options"
            :value="caja ? String(caja.id) : null"
            :searchable="options.length >= SEARCHABLE_FROM"
            :search-label="$t('shell.caja.list')"
            :list-label="$t('shell.caja.list')"
            :empty-text="$t('shell.caja.empty')"
            @select="pick($event, close)"
          >
            <template #option="{ option }">
              <span
                class="caja-dot option-dot"
                :class="`is-${statusById.get(option.value)}`"
              />
            </template>
          </V2SetupPickerList>
        </template>
      </PosUiPopover>

      <!-- Open/closed links to the caja; «pick one» is just a hint (the
           picker next to it is the control). -->
      <router-link
        v-if="cajaPath && status !== 'none'"
        :to="cajaPath"
        class="caja-status bar-item"
        :class="`is-${status}`"
      >
        <span class="caja-dot" :class="`is-${status}`" />
        {{ $t(STATUS_KEYS[status]) }}
      </router-link>
      <span v-else class="caja-status bar-item is-none">
        <span class="caja-dot is-none" />
        {{ $t(STATUS_KEYS.none) }}
      </span>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .v2-shell-context-bar {
    @apply tw-flex tw-flex-wrap tw-items-center tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface tw-px-2 tw-pt-2;
    margin: 8px 12px 0;
    flex: 0 0 auto;

    // Wrapping row without flex gap (Chrome < 84): each item keeps its own
    // bottom and right margin.
    .bar-item {
      @apply tw-mb-2 tw-mr-2 tw-max-w-full;
    }

    .caja-trigger {
      @apply tw-flex tw-h-11 tw-min-w-0 tw-max-w-full tw-cursor-pointer tw-items-center tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-800 tw-transition-colors tw-duration-150;

      &:hover {
        @apply tw-bg-neutral-50;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }

    .trigger-icon {
      @apply tw-mr-2 tw-shrink-0 tw-text-lg tw-text-neutral-500;
    }

    .trigger-label {
      @apply tw-min-w-0 tw-flex-1 tw-truncate tw-text-left;
    }

    .trigger-prefix {
      @apply tw-mr-1 tw-font-normal tw-text-neutral-500;
    }

    .trigger-chevron {
      @apply tw-ml-2 tw-shrink-0 tw-text-lg tw-text-neutral-500;
    }

    .caja-status {
      @apply tw-inline-flex tw-h-8 tw-items-center tw-rounded-full tw-px-3 tw-text-xs tw-font-semibold tw-no-underline;

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      &.is-open {
        @apply tw-bg-emerald-50 tw-text-emerald-700;
      }

      &.is-closed {
        @apply tw-bg-red-50 tw-text-red-700;
      }

      &.is-none {
        @apply tw-bg-amber-50 tw-text-amber-700;
      }

      .caja-dot {
        @apply tw-mr-1.5;
      }
    }
  }

  .caja-dot {
    @apply tw-inline-block tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full;

    &.is-open {
      @apply tw-bg-emerald-500;
    }

    &.is-closed {
      @apply tw-bg-red-500;
    }

    &.is-none {
      @apply tw-bg-amber-500;
    }
  }

  // The dot is slot content of the picker list (this component's scope).
  .option-dot {
    @apply tw-mr-2;
  }
</style>
